import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchTiers, createCheckout } from "../store/slices/subscriptionSlice";
import { CheckIcon } from "@heroicons/react/24/outline";

export default function Pricing() {
  const dispatch = useDispatch();
  const { tiers } = useSelector((state) => state.subscription);
  const { agent } = useSelector((state) => state.auth);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    dispatch(fetchTiers());
  }, [dispatch]);

  const handleSubscribe = (tierId) => {
    if (!agent) {
      window.location.href = "/register";
      return;
    }
    dispatch(createCheckout({ tier: tierId, billing }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Start with a 14-day free trial on the Professional plan. No credit card required.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                billing === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling("yearly")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                billing === "yearly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
              }`}
            >
              Yearly <span className="text-green-600 text-xs font-semibold ml-1">Save 17%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`bg-white rounded-2xl border-2 p-8 relative ${
                tier.popular ? "border-indigo-600 shadow-xl" : "border-gray-200"
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  Most Popular
                </div>
              )}

              <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
              <div className="mt-4 mb-6">
                <span className="text-4xl font-bold text-gray-900">
                  ${billing === "yearly" ? Math.round(tier.yearlyPrice / 12) : tier.monthlyPrice}
                </span>
                <span className="text-gray-500">/month</span>
                {billing === "yearly" && (
                  <p className="text-sm text-green-600 mt-1">
                    ${tier.yearlyPrice}/year (save ${tier.monthlyPrice * 12 - tier.yearlyPrice})
                  </p>
                )}
              </div>

              <p className="text-sm text-gray-600 mb-6">
                {tier.quotesPerMonth === -1
                  ? "Unlimited quotes per month"
                  : `${tier.quotesPerMonth} quotes per month`}
              </p>

              <button
                onClick={() => handleSubscribe(tier.id)}
                className={`w-full py-3 rounded-lg font-semibold text-sm transition-colors ${
                  tier.popular
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                }`}
              >
                {agent ? "Subscribe" : "Start Free Trial"}
              </button>

              <ul className="mt-8 space-y-3">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {!agent && (
          <div className="text-center mt-12">
            <p className="text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="text-indigo-600 font-medium hover:text-indigo-500">
                Sign in
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
